import { connect } from "react-redux";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  getScopesForGroup,
  removeGroupFromScope,
  addGroupToScope,
} from "../../../actions/api";
import { errorAction, types } from "@authzsvc/api-lib";
import { Card, Typography, Form, Checkbox, Spin } from "antd";
import { ShareAltOutlined } from "@ant-design/icons";
import TooltipIcon from "../../common/TooltipIcon";
import { showErrorNotification } from "../../../util";

const GroupScopesSelector = ({
  groupScopes,
  groupId,
  getScopesForGroup,
  removeGroupFromScope,
  addGroupToScope,
}) => {
  const [selectedGroupScopes, setSelectedGroupScopes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      const response = await getScopesForGroup(groupId);
      if (response) {
        setSelectedGroupScopes(response.payload.data);
      }
      setLoading(false);
    };

    getData();
  }, [groupId, getScopesForGroup]);

  const updateScopes = async (updateFunction, scope) => {
    setLoading(true);
    const updateResponse = await updateFunction(groupId, scope.id);
    if (updateResponse && updateResponse.payload) {
      const getResponse = await getScopesForGroup(groupId);
      if (getResponse && getResponse.payload) {
        setSelectedGroupScopes(getResponse.payload.data);
      }
    } else if (
      updateResponse.type === errorAction(types.ADD_GROUP_TO_SCOPE) ||
      updateResponse.type === errorAction(types.REMOVE_GROUP_FROM_SCOPE)
    ) {
      showErrorNotification(
        updateResponse.payload.title,
        updateResponse.payload.data.message
      );
    }
    setLoading(false);
  };

  const handleScopeChange = (scope, e) => {
    const isChecked = e.target.checked;
    isChecked
      ? updateScopes(addGroupToScope, scope)
      : updateScopes(removeGroupFromScope, scope);
  };

  const getDisplayName = (scope) => {
    const displayNameMap = {
      EmailDistributionList: "Mailing List",
      ActiveDirectory:
        "Active Directory: Windows access control, file systems and LDAP",
    };
    return displayNameMap[scope.displayName] || scope.displayName;
  };

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          <ShareAltOutlined style={{ marginRight: 8, color: "#bfbfbf" }} />
          Group usage
          <TooltipIcon
            size={16}
            title="The group will be shared with the systems and services selected."
          />
        </Typography.Title>
      }
      style={{ marginBottom: 24 }}
    >
      <Spin spinning={loading}>
        <Form layout="vertical">
          <Form.Item key={1} style={{ marginBottom: 8 }}>
            <Checkbox checked={true} disabled={true}>
              CERN Single Sign-On
            </Checkbox>
          </Form.Item>
          <i
            style={{ color: "#ff4d4f" }}
            role="note"
            aria-label="Privacy warning"
          >
            When any of the following optional usage settings are selected,
            group information will be sent to an external cloud system that is
            unable to respect group visibility settings.
          </i>
          {groupScopes.map((scope) => (
            <Form.Item key={scope.id} style={{ marginBottom: 8 }}>
              <Checkbox
                checked={selectedGroupScopes.some((s) => s.id === scope.id)}
                onChange={(e) => handleScopeChange(scope, e)}
              >
                {getDisplayName(scope)}{" "}
              </Checkbox>
            </Form.Item>
          ))}
        </Form>
      </Spin>
    </Card>
  );
};

GroupScopesSelector.propTypes = {
  groupId: PropTypes.string.isRequired,
  groupScopes: PropTypes.array.isRequired,
  getScopesForGroup: PropTypes.func.isRequired,
  removeGroupFromScope: PropTypes.func.isRequired,
  addGroupToScope: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({
    groupScopes: state.groupScope,
  }),
  {
    getScopesForGroup,
    removeGroupFromScope,
    addGroupToScope,
  }
)(GroupScopesSelector);
